import { z } from 'zod';
import type { PortalConfig } from '../config/schema.js';
import { CONTACT_FORM_FIELDS } from '../validation/fields.js';
import { VISITOR_LANGUAGES } from '../visitor/texts.js';
import { CONTACT_SOURCES, INTEREST_VALUES, MARKET_CODES, ROLE_VALUES } from '../vocabulary.js';
import {
  CONTACT_FIELDS,
  EVENT_FIELDS,
  EVENT_NOTE_FIELDS,
  EVENT_STATUSES,
  EXPENSE_FIELDS,
  LEAD_FIELDS,
  MEETING_FIELDS,
  QUALIFICATIONS,
  WHERE_FROM,
  type ContactField,
  type EventField,
  type EventNoteField,
  type ExpenseField,
  type LeadField,
  type MeetingField,
} from './fields.js';

/**
 * Validation of the changes a device sends, field by field (sync API). A
 * change may hold any subset of the fields; null (or a blank text) clears a
 * field. Required fields are not checked here: a scanned contact may still
 * miss its company (it then waits in To do). The lead creation answers are
 * checked on the whole lead by the server, with validateLeadCreation.
 */

export type Patch = Record<string, unknown>;
export type PatchResult<F extends string> =
  { ok: true; value: Partial<Record<F, unknown>> } | { ok: false; invalid: string[] };

/** Blank text and null clear a field. */
const blank = (value: unknown) =>
  value === null || (typeof value === 'string' && value.trim() === '')
    ? null
    : typeof value === 'string'
      ? value.trim()
      : value;

const text = (max: number) => z.preprocess(blank, z.string().max(max).nullable());
const oneOf = <V extends readonly [string, ...string[]]>(values: V) =>
  z.preprocess(blank, z.enum(values).nullable());
const uuid = z.preprocess(blank, z.uuid().nullable());
const date = z.preprocess(blank, z.iso.date().nullable());
const markets = z.preprocess(
  (v) => (v === null ? [] : v),
  z.array(z.enum(MARKET_CODES)).refine((list) => new Set(list).size === list.length),
);

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

const linkedin = z.preprocess((value) => {
  const v = blank(value);
  return typeof v === 'string' && !/^[a-z]+:/i.test(v) ? `https://${v}` : v;
}, z.string().max(300).refine(isHttpUrl).nullable());

export function createPatchValidators(
  lists: Pick<PortalConfig['lists'], 'countries'> &
    Partial<Pick<PortalConfig['lists'], 'budgetLines'>>,
) {
  const countries = new Set(lists.countries);
  const country = z.preprocess(
    blank,
    z
      .string()
      .refine((c) => countries.has(c))
      .nullable(),
  );

  const contact: Record<ContactField, z.ZodType> = {
    first_name: text(100),
    last_name: text(100),
    email: z.preprocess(blank, z.email().max(254).nullable()),
    company: text(200),
    job_title: text(200),
    mobile: text(50),
    office_phone: text(50),
    linkedin_url: linkedin,
    country,
    applications: markets,
    interest: oneOf(INTEREST_VALUES),
    role: oneOf(ROLE_VALUES),
    pain: text(2000),
    wants_content: z.boolean(),
    note: text(5000),
    event_id: uuid,
    source: z.enum(CONTACT_SOURCES),
    needs_verification: z
      .array(z.enum(CONTACT_FORM_FIELDS))
      .refine((list) => new Set(list).size === list.length),
    consent_at: z.preprocess(blank, z.iso.datetime({ offset: true }).nullable()),
    consent_text_version: text(50),
    kiosk_asset_ids: z.array(z.uuid()).max(30),
    kiosk_lang: z.enum(VISITOR_LANGUAGES).nullable(),
  };

  const lead: Record<LeadField, z.ZodType> = {
    interest: oneOf(INTEREST_VALUES),
    role: oneOf(ROLE_VALUES),
    pain: text(2000),
    applications: markets,
    country,
    company_name: text(200),
    company_hubspot_id: text(40),
    event_id: uuid,
    deal_name: text(200),
    installation_country: country,
    owner_user_id: uuid,
    stage_id: text(64),
    deal_market: oneOf(MARKET_CODES),
    product_fit: text(2000),
    qualification: oneOf(QUALIFICATIONS),
    disqualified_why: text(2000),
    disqualified_when: text(200),
    project_call_date: date,
    note: text(5000),
    where_from: oneOf(WHERE_FROM),
  };

  const event: Record<EventField, z.ZodType> = {
    series: text(80),
    year: z.int().min(2000).max(2100),
    location: text(200),
    starts_on: date,
    ends_on: date,
    status: z.enum(EVENT_STATUSES),
  };

  const eventNote: Record<EventNoteField, z.ZodType> = {
    event_id: z.uuid(),
    text: text(2000),
    day: date,
    time: z.preprocess(
      blank,
      z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .nullable(),
    ),
    done: z.boolean(),
  };

  // A meeting (Nov 3, 2026): its people as IDs, each once.
  const ids = (max: number) =>
    z.preprocess(
      (v) => (v === null ? [] : v),
      z
        .array(z.uuid())
        .max(max)
        .refine((list) => new Set(list).size === list.length),
    );
  const meeting: Record<MeetingField, z.ZodType> = {
    event_id: uuid,
    met_at: z.iso.datetime({ offset: true }),
    contact_ids: ids(20),
    attendee_ids: ids(20),
    lead_id: uuid,
    notes: text(10000),
    next_step: text(500),
    next_step_due: date,
    title: text(250),
  };

  // A budget line of the list in use; with no list given, any line.
  const budgetLines = lists.budgetLines ? new Set(lists.budgetLines) : null;
  const amount = z.preprocess(
    (v) => (v === '' || v === null ? null : v),
    z
      .number()
      .min(0)
      .max(1_000_000)
      // At most two decimals (cents).
      .refine((n) => Math.abs(n * 100 - Math.round(n * 100)) < 1e-6)
      .nullable(),
  );
  const expense: Record<ExpenseField, z.ZodType> = {
    vendor: text(200),
    spent_on: date,
    currency: z.preprocess(
      blank,
      z
        .string()
        .regex(/^[A-Z]{3}$/)
        .nullable(),
    ),
    total: amount,
    tax_gst: amount,
    tax_qst: amount,
    tax_vat: amount,
    budget_line: z.preprocess(
      blank,
      z
        .string()
        .max(80)
        .refine((b) => !budgetLines || budgetLines.has(b))
        .nullable(),
    ),
    event_id: uuid,
    client: text(200),
    purpose: text(2000),
    guests: text(1000),
    receipt_expected: z.boolean(),
  };

  function validator<F extends string>(fields: readonly F[], schemas: Record<F, z.ZodType>) {
    return (patch: unknown): PatchResult<F> => {
      if (typeof patch !== 'object' || patch === null || Array.isArray(patch)) {
        return { ok: false, invalid: ['(patch)'] };
      }
      const value: Partial<Record<F, unknown>> = {};
      const invalid: string[] = [];
      for (const [key, raw] of Object.entries(patch)) {
        if (!(fields as readonly string[]).includes(key)) {
          invalid.push(key);
          continue;
        }
        const parsed = schemas[key as F].safeParse(raw);
        if (parsed.success) value[key as F] = parsed.data;
        else invalid.push(key);
      }
      return invalid.length > 0 ? { ok: false, invalid } : { ok: true, value };
    };
  }

  return {
    validateContactPatch: validator(CONTACT_FIELDS, contact),
    validateLeadPatch: validator(LEAD_FIELDS, lead),
    validateEventPatch: validator(EVENT_FIELDS, event),
    validateEventNotePatch: validator(EVENT_NOTE_FIELDS, eventNote),
    validateExpensePatch: validator(EXPENSE_FIELDS, expense),
    validateMeetingPatch: validator(MEETING_FIELDS, meeting),
  };
}

export type PatchValidators = ReturnType<typeof createPatchValidators>;
